import { Controller } from '@hotwired/stimulus'
import Cookies from 'js-cookie'
import { matches } from '../hotkeys'

// Detect whether an element is in view inside a parent element.
// Original here: https://gist.github.com/jjmu15/8646226
function isInViewport(element, parentElement) {
  const rect = element.getBoundingClientRect()
  const html = document.documentElement
  const parent = parentElement.getBoundingClientRect()

  return (
    rect.top >= 0
    && rect.left >= 0
    && rect.bottom <= (parent.height || window.innerHeight || html.clientHeight)
    && rect.right <= (parent.width || window.innerWidth || html.clientWidth)
  )
}

// Used on initial page load to scroll to the first active sidebar item if it's not in view.
function scrollSidebarMenuItemIntoView() {
  const activeSidebarItem = document.querySelector('.avo-sidebar .mac-styled-scrollbar a.active')
  const sidebarScrollingArea = document.querySelector('.avo-sidebar .mac-styled-scrollbar')
  if (activeSidebarItem && !isInViewport(activeSidebarItem, sidebarScrollingArea)) {
    activeSidebarItem.scrollIntoView({ block: 'end', inline: 'nearest' })
  }
}

export default class extends Controller {
  static targets = ['sidebar', 'mobileSidebar', 'mainArea']

  static values = {
    open: Boolean,
  }

  get cookieKey() {
    return `${window.Avo.configuration.cookies_key}.sidebar.open`
  }

  get sidebarOpen() {
    return Cookies.get(this.cookieKey) === '1'
  }

  get sidebarScrollPosition() {
    return window.Avo.localStorage.get('sidebar.sidebarScrollPosition')
  }

  set sidebarScrollPosition(value) {
    window.Avo.localStorage.set('sidebar.sidebarScrollPosition', value)
  }

  set cookie(state) {
    Cookies.set(this.cookieKey, state === true ? 1 : 0)
  }

  connect() {
    this.attachScrollVisibilityAnchor()

    // Restore sidebar scroll position
    if (this.sidebarScrollPosition) {
      document.querySelector('.avo-sidebar .mac-styled-scrollbar').scrollTo({
        top: this.sidebarScrollPosition,
        behavior: 'instant',
      })
    }
    this.rememberScrollPosition()

    const isMobile = window.innerWidth < 1024
    const isOpen = isMobile
      ? this.mainAreaTarget.classList.contains('sidebar-mobile-open')
      : this.mainAreaTarget.classList.contains('sidebar-open')
    this.setToggleButtonsState(isOpen ? 'open' : 'closed')

    this.handleToggleShortcut = (event) => {
      if (event.repeat || event.defaultPrevented) return
      if (!matches(event, 'sidebar.toggle')) return

      event.preventDefault()
      this.toggleSidebarForViewport()
    }
    document.addEventListener('keydown', this.handleToggleShortcut)
  }

  disconnect() {
    document.removeEventListener('keydown', this.handleToggleShortcut)
  }

  rememberScrollPosition() {
    let handler

    document.addEventListener('turbo:visit', handler = () => {
      // Remember sidebar scroll position before changing pages.
      this.sidebarScrollPosition = document.querySelector('.avo-sidebar .mac-styled-scrollbar').scrollTop
      // remove event handler after disconnection
      document.removeEventListener('turbo:visit', handler)
    })
  }

  attachScrollVisibilityAnchor() {
    scrollSidebarMenuItemIntoView()
  }

  get toggleButtons() {
    return document.querySelectorAll('[data-sidebar-toggle-icon]')
  }

  setToggleButtonsState(state) {
    this.toggleButtons.forEach((button) => {
      button.dataset.sidebarState = state
    })
  }

  toggleSidebarForViewport() {
    if (window.innerWidth < 1024) {
      this.toggleSidebarOnMobile()
    } else {
      this.toggleSidebar()
    }
  }

  toggleSidebar() {
    if (this.sidebarTarget.classList.contains('hidden')) {
      this.sidebarTarget.classList.remove('hidden')

      // The sidebar starts the session as display:none when closed. Removing
      // `hidden` and toggling `sidebar-open` in the same tick would let the
      // browser collapse both style changes into one repaint — meaning the
      // transform: translateX transition would never fire on the very first
      // open. Force a reflow so the browser commits the post-hidden state
      // (translateX(-100%) visible) before we flip to translateX(0).
      this.mainAreaTarget.offsetHeight // eslint-disable-line no-unused-expressions
    }
    this.mainAreaTarget.classList.toggle('sidebar-open')

    Cookies.set(this.cookieKey, this.newValue(Cookies.get(this.cookieKey)))

    const isOpen = this.mainAreaTarget.classList.contains('sidebar-open')
    // Keep the controller's openValue in sync so --sidebar-offset-size flips,
    // which drives the navbar spacer width and .main padding transitions.
    this.openValue = isOpen
    this.setToggleButtonsState(isOpen ? 'open' : 'closed')
  }

  toggleSidebarOnMobile() {
    if (this.mobileSidebarTarget.classList.contains('hidden')) {
      this.mainAreaTarget.classList.remove('sidebar-mobile-open')
      this.mobileSidebarTarget.classList.remove('hidden')

      // we force a reflow here because we remove then
      // immediately add the sidebar-mobile-open class
      // which doesn't give the browser enough time to apply the
      // transition.
      this.mainAreaTarget.offsetHeight // eslint-disable-line no-unused-expressions
    }
    this.mainAreaTarget.classList.toggle('sidebar-mobile-open')

    const isOpen = this.mainAreaTarget.classList.contains('sidebar-mobile-open')
    this.setToggleButtonsState(isOpen ? 'open' : 'closed')
  }

  // private
  newValue(oldValue) {
    if (oldValue === undefined) {
      return '0'
    }

    return oldValue === '1' ? '0' : '1'
  }
}
